/* AdminCommunity — moderator queue for today's community (questions, answers, private reports, suspensions, settings). */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Lock, Unlock, EyeOff, CheckCircle2, Ban, Clock3 } from 'lucide-react';
import { api, errorInfo } from '../lib/communityApi';

const STATE = { published: 'bg-emerald-100 text-emerald-800', pending: 'bg-amber-100 text-amber-800', rejected: 'bg-rose-100 text-rose-800', removed: 'bg-gray-200 text-gray-600' };
const Btn = ({ onClick, children, tone = 'gray', testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1.5 border transition-colors ${tone === 'red' ? 'border-rose-200 text-rose-700 hover:bg-rose-50' : tone === 'green' ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{children}</button>
);

export default function AdminCommunity() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    try { const r = await api.adminQueue(); setD(r); setOffset(r.test_clock?.offset_seconds || 0); setErr(''); }
    catch (e) { setErr(errorInfo(e).message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { setErr(errorInfo(e).message); } setBusy(false); };
  const mod = (type, id, action, extra = {}) => {
    const reason = (window.prompt(`Reason for "${action}" (min 3 characters — recorded in the audit log, shown to the member):`) || '').trim();
    if (!reason) return;
    if (reason.length < 3) { setErr('Please give a reason of at least 3 characters.'); return; }
    run(() => api.moderate(type, id, { action, reason, ...extra }));
  };

  if (!d) return <p className="text-sm text-gray-500">{err || 'Loading community queue…'}</p>;
  const s = d.settings;

  return (
    <div className="space-y-5" data-testid="admin-community">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="font-bold text-gray-800 inline-flex items-center gap-1"><Clock3 size={13} /> Community day {d.community_day_key} · resets {new Date(d.resets_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</span>
        <button type="button" onClick={load} className="inline-flex items-center gap-1 font-bold text-teal-700" data-testid="admin-community-refresh"><RefreshCw size={12} /> Refresh</button>
        {busy && <Loader2 size={14} className="animate-spin text-gray-400" />}
        {err && <span className="text-rose-700 font-semibold" data-testid="admin-community-error">{err}</span>}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-gray-200 p-3 text-xs space-y-2" data-testid="admin-community-settings">
          <p className="font-bold text-gray-900">Settings</p>
          <label className="flex items-center justify-between gap-2">Submissions enabled
            <input type="checkbox" checked={!!s.submissions_enabled} onChange={(e) => run(() => api.settings({ submissions_enabled: e.target.checked }))} data-testid="admin-community-submissions-toggle" /></label>
          <label className="flex items-center justify-between gap-2">Moderation mode
            <select value={s.moderation_mode} onChange={(e) => run(() => api.settings({ moderation_mode: e.target.value }))} className="border rounded px-1 py-0.5" data-testid="admin-community-mode-select">
              <option value="standard">standard (publish, moderate after)</option><option value="pilot">pilot (hold for review)</option>
            </select></label>
          <p className="text-gray-500">3 questions + 3 answers per account and per browser/day. Moderator: existing admin accounts.</p>
        </div>
        <div className="rounded-2xl border border-gray-200 p-3 text-xs" data-testid="admin-community-suspended">
          <p className="font-bold text-gray-900 mb-1">Suspended members ({d.suspended.length})</p>
          {d.suspended.length === 0 && <p className="text-gray-500">None.</p>}
          {d.suspended.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between gap-2 py-1 border-t border-gray-100">
              <span className="truncate">…{m.user_id.slice(-6)} · until {new Date(m.posting_suspension_until).toLocaleDateString('en-IN')}</span>
              <Btn tone="green" onClick={() => run(() => api.suspend({ user_id: m.user_id, days: 0, reason: 'Suspension ended by moderator' }))}>End</Btn>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-3 text-xs" data-testid="admin-community-test-clock">
          <p className="font-bold text-amber-900">Test clock {d.test_clock?.enabled ? '(Preview only)' : '(disabled)'}</p>
          <p className="text-amber-800">Shift the community's server clock to rehearse midnight. Never enabled in production.</p>
          {d.test_clock?.enabled && (
            <div className="mt-2 flex items-center gap-2">
              <input type="number" value={offset} onChange={(e) => setOffset(Number(e.target.value))} className="w-28 border rounded px-2 py-1" data-testid="admin-community-clock-input" />
              <Btn onClick={() => run(() => api.testClock(offset))} testId="admin-community-clock-apply">Apply offset (s)</Btn>
              <Btn onClick={() => run(() => api.testClock(0))} testId="admin-community-clock-reset">Reset</Btn>
            </div>
          )}
        </div>
      </div>

      <div>
        <p className="text-sm font-extrabold text-gray-900 mb-2">Open reports ({d.reports.length})</p>
        {d.reports.length === 0 && <p className="text-xs text-gray-500">No open reports.</p>}
        <div className="space-y-2" data-testid="admin-community-reports">
          {d.reports.map((r) => (
            <div key={r.id} className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs flex flex-wrap items-center gap-2" data-testid={`admin-report-${r.id}`}>
              <span className="font-bold text-rose-800">{r.reason_id}</span>
              <span className="text-gray-700">{r.target_type} {r.target_id}</span>
              {r.details && <span className="text-gray-600 italic">“{r.details}”</span>}
              <span className="text-gray-500">reporter {r.reporter_user_id}</span>
              {r.target_expired && <span className="font-bold text-gray-600 bg-gray-200 rounded-full px-2">target expired</span>}
              <span className="ml-auto flex gap-1">
                <Btn tone="red" onClick={() => mod(r.target_type, r.target_id, 'hide')} testId={`admin-report-hide-${r.id}`}><EyeOff size={11} /> Hide</Btn>
                <Btn onClick={() => mod(r.target_type, r.target_id, 'warn')}>Dismiss (keep)</Btn>
              </span>
            </div>
          ))}
        </div>
      </div>

      {[['Questions', 'question', d.questions], ['Answers', 'answer', d.answers]].map(([label, type, items]) => (
        <div key={type}>
          <p className="text-sm font-extrabold text-gray-900 mb-2">Today's {label.toLowerCase()} ({items.length})</p>
          {items.length === 0 && <p className="text-xs text-gray-500">None today.</p>}
          <div className="space-y-2" data-testid={`admin-community-${type}s`}>
            {items.map((x) => (
              <div key={x.id} className="rounded-xl border border-gray-200 p-3 text-xs" data-testid={`admin-${type}-${x.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-bold rounded-full px-2 py-0.5 ${STATE[x.content_state]}`} data-testid={`admin-${type}-state-${x.id}`}>{x.content_state}</span>
                  {x.thread_state === 'locked' && <span className="font-bold rounded-full px-2 py-0.5 bg-gray-200 text-gray-700">locked</span>}
                  {x.topic_id && <span className="text-gray-500">{x.topic_id}</span>}
                  <span className="text-gray-400">author …{x.author_ref}</span>
                  <span className="text-gray-400">{new Date(x.created_at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</span>
                </div>
                {x.title && <p className="font-bold text-gray-900 mt-1 break-words">{x.title}</p>}
                <p className="text-gray-700 mt-0.5 whitespace-pre-wrap break-words line-clamp-3">{x.body}</p>
                {x.moderation_reason && <p className="text-amber-800 mt-1">Reason: {x.moderation_reason}</p>}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {x.content_state === 'pending' && <Btn tone="green" onClick={() => mod(type, x.id, 'approve')} testId={`admin-approve-${x.id}`}><CheckCircle2 size={11} /> Approve</Btn>}
                  {x.content_state === 'pending' && <Btn tone="red" onClick={() => mod(type, x.id, 'reject')} testId={`admin-reject-${x.id}`}>Reject</Btn>}
                  {x.content_state === 'published' && <Btn tone="red" onClick={() => mod(type, x.id, 'hide')} testId={`admin-hide-${x.id}`}><EyeOff size={11} /> Hide</Btn>}
                  {x.content_state === 'removed' && <Btn onClick={() => mod(type, x.id, 'restore_review')} testId={`admin-restore-${x.id}`}>Review for restoration</Btn>}
                  {type === 'question' && x.thread_state === 'open' && <Btn onClick={() => mod(type, x.id, 'lock')} testId={`admin-lock-${x.id}`}><Lock size={11} /> Lock replies</Btn>}
                  {type === 'question' && x.thread_state === 'locked' && <Btn onClick={() => mod(type, x.id, 'unlock')} testId={`admin-unlock-${x.id}`}><Unlock size={11} /> Unlock</Btn>}
                  <Btn tone="red" onClick={() => { const reason = window.prompt('Suspension reason (7 days):'); if (reason) run(() => api.suspend({ user_id: x.author_user_id, days: 7, reason })); }} testId={`admin-suspend-${x.id}`}><Ban size={11} /> Suspend author 7d</Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
